/**
 * SignaturePad — the drawn signature, on a phone, with a finger.
 *
 * ── THE MIN-INK GATE ──────────────────────────────────────────────────────────
 * `signature_pad` calls a single accidental tap a signature. A legal instrument should
 * not. Submit stays disabled until the drawing clears two independent floors, measured next
 * door in `./signature-ink`:
 *
 *   · PATH  — the pen has to travel at least `MIN_PATH_PX` in total, so one dot is not a
 *             signature;
 *   · REACH — the ink's bounding box has to span at least `MIN_SPAN_PX` across, so a
 *             scribble in one spot is not one either.
 *
 * Two floors rather than one because either alone is trivially satisfied: a long slow press
 * produces a long path inside one spot, and a fast flick produces a wide box from barely any
 * travel. Both are the shapes an accidental touch actually makes.
 *
 * THE FIRST FLOOR MEASURES DISTANCE, NOT SAMPLES, and that is the whole defect it was born
 * from. `signature_pad` decimates twice before it stores anything — one sample per 16ms
 * (`throttle`) and nothing closer than 5px to the last kept one (`minDistance`) — then paints
 * a Bezier through the survivors. A stored-point count therefore measures how LONG the finger
 * moved and how fast the device fired pointer events, not how much ink is on the pad: a full,
 * wide signature drawn in ~300ms paints complete and stores ~16 points, and failed a 24-point
 * floor. Path length is the quantity that floor was always standing in for, and it is
 * invariant to both decimations.
 *
 * ── THE CANVAS IS ALWAYS PAPER ────────────────────────────────────────────────
 * The app is monochrome and theme-aware; this one surface is not. The PNG is composited
 * into a printed page, so it is drawn in black on white regardless of theme, and the
 * frame around it says so.
 *
 * ── RESOLUTION ────────────────────────────────────────────────────────────────
 * The backing store is scaled by `devicePixelRatio` (capped, so a 3x phone does not
 * produce a needlessly large PNG) and the pad is re-fitted on resize. Re-fitting clears
 * the canvas — that is what `signature_pad` does — so the existing strokes are replayed
 * from `toData()` afterwards rather than being lost on an orientation change.
 */

import { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import SignaturePadLib from 'signature_pad';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { inkClearsFloors } from './signature-ink';

/** A 3x backing store buys nothing for a black-ink trace and costs kilobytes. */
const MAX_PIXEL_RATIO = 2;

export interface SignaturePadHandle {
    /** `data:image/png;base64,…`, or null when the pad is empty. */
    toDataUrl: () => string | null;
    clear: () => void;
}

interface Props {
    /** Fires whenever the ink crosses (or falls back below) the min-ink floors. */
    onInkChange?: (hasEnoughInk: boolean) => void;
    /** Small caption under the pad — date and place, as a wet signature would carry. */
    caption?: string;
    disabled?: boolean;
    className?: string;
}

const SignaturePad = forwardRef<SignaturePadHandle, Props>(function SignaturePad(
    { onInkChange, caption, disabled, className },
    ref,
) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const padRef = useRef<SignaturePadLib | null>(null);
    const [hasInk, setHasInk] = useState(false);

    // Held in a ref, not a dependency: the mount effect below RE-CREATES the pad when its
    // deps change, and re-creating the pad clears the canvas. An inline `onInkChange` from
    // the parent would therefore erase a signature on every parent render.
    const onInkChangeRef = useRef(onInkChange);
    useEffect(() => {
        onInkChangeRef.current = onInkChange;
    }, [onInkChange]);

    /** Puts everything drawn so far through the two floors. */
    const measure = useCallback(() => {
        const pad = padRef.current;
        if (!pad || pad.isEmpty()) return false;
        return inkClearsFloors(pad.toData());
    }, []);

    const report = useCallback(() => {
        const enough = measure();
        setHasInk((previous) => (previous === enough ? previous : enough));
        onInkChangeRef.current?.(enough);
    }, [measure]);

    // Mount the pad once; re-fit on resize, replaying what was already drawn.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const pad = new SignaturePadLib(canvas, {
            backgroundColor: 'rgba(255,255,255,0)',
            penColor: '#0A0A0B',
            minWidth: 0.9,
            maxWidth: 2.6,
        });
        padRef.current = pad;

        const fit = () => {
            const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
            const strokes = pad.toData();
            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = canvas.offsetHeight * ratio;
            canvas.getContext('2d')?.scale(ratio, ratio);
            // Resizing the backing store clears it; put the strokes back.
            pad.clear();
            if (strokes.length > 0) pad.fromData(strokes);
        };

        fit();
        pad.addEventListener('endStroke', report);
        window.addEventListener('resize', fit);
        window.addEventListener('orientationchange', fit);

        return () => {
            pad.removeEventListener('endStroke', report);
            window.removeEventListener('resize', fit);
            window.removeEventListener('orientationchange', fit);
            pad.off();
            padRef.current = null;
        };
    }, [report]);

    useEffect(() => {
        if (disabled) padRef.current?.off();
        else padRef.current?.on();
    }, [disabled]);

    useImperativeHandle(ref, () => ({
        toDataUrl: () => {
            const pad = padRef.current;
            if (!pad || pad.isEmpty()) return null;
            return pad.toDataURL('image/png');
        },
        clear: () => {
            padRef.current?.clear();
            report();
        },
    }), [report]);

    return (
        <div className={cn('space-y-2', className)}>
            <div className="relative rounded-xl border border-[#DDE3ED] bg-white p-2.5">
                <canvas
                    ref={canvasRef}
                    aria-label="Signature pad — draw your signature"
                    className="block h-[168px] w-full cursor-crosshair touch-none rounded-lg"
                />
                <div className="pointer-events-none absolute inset-x-6 bottom-[42px] border-t border-dashed border-[#C6CEDC]" />
                <span className="pointer-events-none absolute bottom-[33px] left-5 text-[15px] font-bold text-[#9AA5B8]">
                    ✕
                </span>
                {!hasInk && (
                    <p className="pointer-events-none absolute inset-x-0 bottom-3.5 text-center text-[11px] text-[#9AA5B8]">
                        Draw your signature above the line
                    </p>
                )}
            </div>
            <div className="flex items-center justify-between">
                <span className="text-[11.5px] text-muted-foreground">{caption}</span>
                <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs"
                    onClick={() => {
                        padRef.current?.clear();
                        report();
                    }}
                >
                    Clear
                </Button>
            </div>
        </div>
    );
});

export default SignaturePad;
